import { format } from 'date-fns';
import { unwrap, type ActivityListRow } from '@billda/api';
import { api } from '../../../shared/api/billdaApi';
import type {
  ActivityFilters,
  ActivityRow,
  ActivityStatus,
  ActivityItemType,
} from '../types';

export interface ActivityPage {
  items: ActivityRow[];
  totalCount: number;
  hasMore: boolean;
}

export type ActivityIncludeMode = 'booked-approved' | 'approved';

/**
 * `to`/`from` are org-local calendar dates; the server interprets them in the org
 * timezone. Format from the local calendar, not `toISOString().slice(0,10)` — the
 * latter shifts the day back for UTC+ users (e.g. UK during BST).
 */
function toDateParam(iso: string): string {
  return format(new Date(iso), 'yyyy-MM-dd');
}

const ACTIVITY_STATUSES: readonly ActivityStatus[] = [
  'Booked',
  'Approved',
  'Rejected',
  'Cancelled',
];

/** The contract types the status as a plain string; unknown values read as Booked. */
function toStatus(value: string): ActivityStatus {
  return ACTIVITY_STATUSES.find(status => status === value) ?? 'Booked';
}

function toItemType(value: string): ActivityItemType {
  return value === 'Variation' ? 'Variation' : 'Labour';
}

function mapRow(row: ActivityListRow): ActivityRow {
  return {
    id: row.allocationId,
    worker: {
      type: row.worker.type === 'Gang' ? 'Gang' : 'Solo',
      displayName: row.worker.name,
      memberNames: row.worker.members,
    },
    status: toStatus(row.status),
    itemType: toItemType(row.type),
    // Variations read as their work type, with the free-text entry demoted to
    // the hover card. Labour rows carry the definition name and no subtitle.
    title: row.workTypeName ?? row.work,
    subtitle: row.workTypeName ? row.work : null,
    approval: row.approval
      ? {
          isApproved: row.approval.isApproved,
          reviewedBy: row.approval.reviewedBy,
          reviewedAt: row.approval.reviewedAt,
          rejectionReason: row.approval.rejectionReason,
          rejectionNotes: row.approval.rejectionNotes,
        }
      : null,
    reference: row.reference,
    plotName: row.plotNumber,
    siteName: row.siteName,
    bookedAt: row.bookedAt,
    value: row.bookedValue,
    fullValue: row.labourItemValue,
    isPartialBooking: row.isPartialBooking,
    quantityClaimed: row.quantityClaimed,
    unitRate: row.unitRate,
    unitLabel: row.unitLabel,
    siteId: row.siteId,
    plotId: row.plotId,
  };
}

function listQuery(filters: ActivityFilters, offset: number, take: number) {
  return {
    offset,
    take,
    from: filters.from ? toDateParam(filters.from) : undefined,
    to: filters.to ? toDateParam(filters.to) : undefined,
    statuses: filters.status ? [filters.status] : undefined,
    types: filters.type ? [filters.type] : undefined,
    siteIds: filters.siteIds.length > 0 ? filters.siteIds : undefined,
    reviewableOnly: filters.reviewableOnly,
    sortDirection: filters.sortDirection,
  };
}

export async function fetchActivityList(
  filters: ActivityFilters,
  offset: number,
  take: number,
  signal?: AbortSignal
): Promise<ActivityPage> {
  const response = unwrap(
    await api.pricework.GET('/pricework/api/v1/activity', {
      params: { query: listQuery(filters, offset, take) },
      signal,
    })
  );
  return {
    items: response.items.map(mapRow),
    totalCount: response.totalCount,
    hasMore: response.hasMore,
  };
}

export interface ExportActivityParams {
  from: string;
  to: string;
  siteIds: number[];
  type: '' | 'Labour' | 'Variation';
  include: ActivityIncludeMode;
}

function exportQuery(params: ExportActivityParams) {
  return {
    from: toDateParam(params.from),
    to: toDateParam(params.to),
    include: params.include,
    types: params.type ? [params.type] : undefined,
    siteIds: params.siteIds.length > 0 ? params.siteIds : undefined,
  };
}

/** The server names the file; fall back to the requested window if it doesn't. */
function csvFilename(response: Response, params: ExportActivityParams): string {
  const disposition = response.headers.get('content-disposition');
  const match = disposition?.match(/filename="?([^";\n]+)"?/);
  return (
    match?.[1] ??
    `activity_${toDateParam(params.from)}_${toDateParam(params.to)}.csv`
  );
}

function saveBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function downloadActivityCsv(
  params: ExportActivityParams
): Promise<void> {
  const result = await api.pricework.GET('/pricework/api/v1/activity/export', {
    params: { query: exportQuery(params) },
    parseAs: 'blob',
  });
  saveBlob(unwrap(result), csvFilename(result.response, params));
}
